import { TableCell } from '@mui/material';
import styled from 'styled-components';
import { fadeInKeyframes } from './animations';
import { COLORS, SIZES, TIMINGS } from './constants';

export const TableHeadCell = styled(TableCell)`
  padding: 0.2rem 0.4rem !important;
  position: relative;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: auto;
  min-height: 0;
  vertical-align: middle;

  &:not(:last-child)::after {
    content: '';
    height: ${SIZES.separatorHeight};
    width: ${SIZES.separatorWidth};
    position: absolute;
    margin-left: 20px;
    right: 0px;
    top: 36%;
    bottom: 36%;
    background-color: ${COLORS.dividerGray};
  }
`;

export const HoverableTableHeadCell = styled(TableCell)<{
  $canHide: boolean;
  $isHiding: boolean;
  $isShowing: boolean;
}>`
  padding: 0.2rem 0.4rem !important;
  position: relative;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: visible;
  text-overflow: ellipsis;
  cursor: default;
  height: auto;
  min-height: 0;
  vertical-align: middle;
  transition: background-color ${TIMINGS.transitionFast} ease,
    color ${TIMINGS.transitionFast} ease,
    opacity ${TIMINGS.transitionMedium} ease;

  ${(props) =>
    props.$canHide &&
    `
    &:hover {
      background-color: ${COLORS.columnHover};
      color: ${COLORS.textGray};
    }
  `}

  ${(props) =>
    props.$isHiding &&
    `
    opacity: 0;
    transition: opacity ${TIMINGS.transitionMedium} ease;
  `}
  
  ${(props) =>
    props.$isShowing &&
    `
    ${fadeInKeyframes}
  `}

  &:not(:last-child)::after {
    content: '';
    height: ${SIZES.separatorHeight};
    width: ${SIZES.separatorWidth};
    position: absolute;
    margin-left: 20px;
    right: 0px;
    top: 36%;
    bottom: 36%;
    background-color: ${COLORS.dividerGray};
    transition: opacity ${TIMINGS.transitionMedium} ease;

    ${(props) =>
      (props.$isHiding || props.$isShowing) &&
      `
      opacity: 0;
    `}
  }
`;

export const HideColumnIcon = styled.div`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  display: none;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 50%;
  padding: 4px;
  z-index: 10;
  cursor: pointer;
  transition: opacity ${TIMINGS.transitionFast} ease;

  .MuiSvgIcon-root {
    color: ${COLORS.white};
    font-size: 10px;
  }

  th:hover & {
    display: flex;
  }

  &:hover {
    background: ${COLORS.iconHover};
  }
`;

export const StickyCheckboxCell = styled(TableCell)<{
  $deletionStatus?: 'loading' | 'success' | 'success-other' | 'error' | null;
}>`
  position: sticky;
  left: 0;
  z-index: 40;
  background-color: ${COLORS.white};
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
  isolation: isolate;
  padding: 0.2rem 0.4rem !important;
  height: auto;
  min-height: 0;
  vertical-align: middle;

  .MuiCheckbox-root {
    padding: 0px !important;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
      box-shadow: inset 0 0 0 2px ${COLORS.loading},
        0 0 0 2px rgba(158, 158, 158, 0.3), 2px 0 4px rgba(0, 0, 0, 0.05);
    }
    50% {
      opacity: 0.7;
      box-shadow: inset 0 0 0 2px ${COLORS.borderGray},
        0 0 0 3px rgba(224, 224, 224, 0.5), 2px 0 4px rgba(0, 0, 0, 0.05);
    }
  }

  ${(props) =>
    props.$deletionStatus === 'loading' &&
    `
    background: ${COLORS.white};
    box-shadow: inset 0 0 0 2px ${COLORS.loading}, 0 0 0 2px rgba(158, 158, 158, 0.3);
    animation: pulse ${TIMINGS.animationPulse} ease-in-out infinite;
    &::after { background: ${COLORS.white} !important; }
  `}

  ${(props) =>
    props.$deletionStatus === 'success' &&
    `
    background: ${COLORS.white};
    box-shadow: inset 0 0 0 2px ${COLORS.success}, 0 0 0 3px rgba(76, 175, 80, 0.3);
    transition: box-shadow ${TIMINGS.transitionNormal} ease;
    &::after { background: ${COLORS.white} !important; }
  `}

  ${(props) =>
    props.$deletionStatus === 'success-other' &&
    `
    background: ${COLORS.white};
    box-shadow: inset 0 0 0 2px ${COLORS.loading}, 0 0 0 3px rgba(158, 158, 158, 0.2);
    transition: box-shadow ${TIMINGS.transitionNormal} ease;
    &::after { background: ${COLORS.white} !important; }
  `}

  ${(props) =>
    props.$deletionStatus === 'error' &&
    `
    background: ${COLORS.white};
    box-shadow: inset 0 0 0 2px ${COLORS.errorLight}, 0 0 0 3px rgba(244, 67, 54, 0.3);
    transition: box-shadow ${TIMINGS.transitionNormal} ease;
    &::after { background: ${COLORS.white} !important; }
  `}

  thead & {
    z-index: 100;
    top: 0;
    background-color: ${COLORS.white};
  }

  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: rgba(224, 224, 224, 0.5);
  }

  tr:hover:not([data-selected='true']):not([data-date-changed='true']):not([data-shipping-line-transport='true']):not(thead)
    & {
    background: #f2f2f2;
    &::after {
      background: #f2f2f2;
    }
  }

  tr[data-shipping-line-transport='true'] & {
    background: ${COLORS.shippingLineHaulageBg};
    &::after {
      background: ${COLORS.shippingLineHaulageBg};
    }
  }

  tr[data-shipping-line-transport='true']:hover & {
    background: ${COLORS.shippingLineHaulageHover};
    &::after {
      background: ${COLORS.shippingLineHaulageHover};
    }
  }

  tr[data-date-changed='true'] & {
    background: ${COLORS.dateChangedBg};
    &::after {
      background: ${COLORS.dateChangedBg};
    }
  }

  tr[data-date-changed='true']:hover & {
    background: ${COLORS.dateChangedHover};
    &::after {
      background: ${COLORS.dateChangedHover};
    }
  }

  tr[data-date-change-accepted='true'] & {
    background: ${COLORS.dateChangedAcceptedBg};
    &::after {
      background: ${COLORS.dateChangedAcceptedBg};
    }
  }

  tr[data-date-change-accepted='true']:hover & {
    background: ${COLORS.dateChangedAcceptedBgHover};
    &::after {
      background: ${COLORS.dateChangedAcceptedBgHover};
    }
  }

  tr[data-selected='true'] & {
    background: ${COLORS.selectedLight};
    &::after {
      background: ${COLORS.selectedLightHover};
    }
  }
  tr[data-selected='true']:hover & {
    background: ${COLORS.selectedLightHover};
    &::after {
      background: ${COLORS.selectedLightHover};
    }
  }

  tr[data-date-changed='true'][data-selected='true'] & {
    background: ${COLORS.dateChangedBg};
    &::after {
      background: ${COLORS.dateChangedBg};
    }
  }
  tr[data-date-changed='true'][data-selected='true']:hover & {
    background: ${COLORS.dateChangedHover};
    &::after {
      background: ${COLORS.dateChangedHover};
    }
  }

  tr[data-date-change-accepted='true'][data-selected='true'] & {
    background: ${COLORS.dateChangedAcceptedBg};
    &::after {
      background: ${COLORS.dateChangedAcceptedBg};
    }
  }
  tr[data-date-change-accepted='true'][data-selected='true']:hover & {
    background: ${COLORS.dateChangedAcceptedBgHover};
    &::after {
      background: ${COLORS.dateChangedAcceptedBgHover};
    }
  }

  tr[data-deleted='true'] &,
  tr[data-deleted='true']:hover & {
    background: ${COLORS.deletedDark} !important;
    &::after {
      background: ${COLORS.deletedDark} !important;
    }
  }
`;
